import React, { useEffect, useMemo, useState } from 'react'
import Select from 'react-select'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import { httpOk, GET, POST, PUT } from 'helpers/api'

const emptyResult = {
	stats: null,
	catalog: [],
}

function displayValue(value) {
	if (value === null || value === undefined || value === '') return '—'
	return String(value)
}

function CellValue({ current, suggested, changed }) {
	if (!changed) {
		return <span>{displayValue(current)}</span>
	}

	return (
		<div className="ai-cell-changed">
			<div className="ai-cell-old">{displayValue(current)}</div>
			<div className="ai-cell-new">→ {displayValue(suggested)}</div>
		</div>
	)
}

function getField(product, key) {
	return (product.fields || []).find(field => field.key === key) || {
		key,
		current: product[key],
		suggested: null,
		changed: false,
	}
}

function ExternalMenuAiModal({ show, onHide, externalGroupId, onApplied }) {
	const { t } = useTranslation()
	const [languages, setLanguages] = useState([])
	const [language, setLanguage] = useState(null)
	const [loading, setLoading] = useState(false)
	const [applying, setApplying] = useState(false)
	const [onlyChanges, setOnlyChanges] = useState(false)
	const [result, setResult] = useState(emptyResult)

	async function getLanguages() {
		const response = await GET('/helper/languages')
		if (httpOk(response)) {
			const list = response.data || []
			setLanguages(list)
			const ru = list.find(item => item.app_code === 'ru' || item.code === 'ru')
			setLanguage(ru || list[0] || null)
		}
	}

	function resetResult() {
		setResult(emptyResult)
	}

	async function runAi() {
		if (!language || !externalGroupId) return

		setLoading(true)
		resetResult()

		const response = await POST(`/external-menu-ai/${externalGroupId}`, {
			mode: 'all',
			language: language.app_code || language.code,
		}, { loader: true, notification: false })

		setLoading(false)

		if (httpOk(response)) {
			const catalog = (response.data.catalog || []).map(menu => ({
				...menu,
				selected: !!(menu.delete_suggested || menu.has_name_change),
				products: (menu.products || []).map(product => ({
					...product,
					selected: !!product.has_changes,
				})),
			}))

			setResult({
				stats: response.data.stats || null,
				catalog,
			})
		}
	}

	function toggleMenu(menuId) {
		setResult(prev => ({
			...prev,
			catalog: prev.catalog.map(menu =>
				menu.id === menuId && (menu.delete_suggested || menu.has_name_change)
					? { ...menu, selected: !menu.selected }
					: menu
			),
		}))
	}

	function toggleProduct(menuId, productId) {
		setResult(prev => ({
			...prev,
			catalog: prev.catalog.map(menu => {
				if (menu.id !== menuId) return menu
				return {
					...menu,
					products: menu.products.map(product =>
						product.id === productId && product.has_changes
							? { ...product, selected: !product.selected }
							: product
					),
				}
			}),
		}))
	}

	function selectAllChanges(selected) {
		setResult(prev => ({
			...prev,
			catalog: prev.catalog.map(menu => ({
				...menu,
				selected: (menu.delete_suggested || menu.has_name_change) ? selected : false,
				products: menu.products.map(product => ({
					...product,
					selected: product.has_changes ? selected : false,
				})),
			})),
		}))
	}

	function buildApplyPayload() {
		const menus = []
		const products = []
		const delete_menus = []
		const delete_products = []

		result.catalog.forEach(menu => {
			if (menu.selected && menu.delete_suggested) {
				delete_menus.push(menu.id)
				return
			}

			if (menu.selected && menu.has_name_change && menu.suggested_name) {
				menus.push({ id: menu.id, name: menu.suggested_name })
			}

			menu.products.forEach(product => {
				if (!product.selected || !product.has_changes) return

				if (product.delete_suggested) {
					delete_products.push(product.id)
					return
				}

				const row = { id: product.id }
				if (product.suggested_name != null) row.name = product.suggested_name
				if (product.suggested_description != null) row.description = product.suggested_description
				if (product.suggested_weight != null) row.weight = product.suggested_weight
				if (product.suggested_unit != null) row.unit = product.suggested_unit
				if (Object.keys(row).length > 1) products.push(row)
			})
		})

		return { menus, products, delete_menus, delete_products }
	}

	async function applyChanges() {
		const payload = buildApplyPayload()
		if (
			!payload.menus.length
			&& !payload.products.length
			&& !payload.delete_menus.length
			&& !payload.delete_products.length
		) return

		setApplying(true)
		const response = await PUT(`/external-menu-ai/${externalGroupId}`, payload, {
			loader: true,
			notification: true,
		})
		setApplying(false)

		if (httpOk(response)) {
			onApplied?.()
			onHide?.()
		}
	}

	const selectedCount = useMemo(() => {
		let count = 0
		result.catalog.forEach(menu => {
			if (menu.selected && (menu.delete_suggested || menu.has_name_change)) count++
			menu.products.forEach(product => {
				if (product.selected && product.has_changes) count++
			})
		})
		return count
	}, [result.catalog])

	const visibleCatalog = useMemo(() => {
		if (!onlyChanges) return result.catalog
		return result.catalog
			.map(menu => ({
				...menu,
				products: menu.products.filter(product => product.has_changes),
			}))
			.filter(menu => menu.has_changes || menu.products.length > 0)
	}, [result.catalog, onlyChanges])

	useEffect(() => {
		if (show) {
			getLanguages()
			resetResult()
			setOnlyChanges(false)
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Modal
			show={show}
			animation={false}
			centered
			size="xl"
			dialogClassName="ai-menu-modal"
			onHide={onHide}>
			<Modal.Header closeButton>
				<Modal.Title>
					AI · {t('external_menu')}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<style>{`
					/* Окно по высоте экрана: таблица листается внутри, подвал с «Применить»
					   всегда виден. Раньше окно росло под таблицу, и на ноутбуке кнопка
					   уезжала за край — AI отрабатывал, а применить было нечем */
					.modal-dialog.ai-menu-modal {
						max-width: 1140px;
						height: calc(100vh - 2rem);
						margin: 1rem auto;
					}
					.ai-menu-modal .modal-content {
						height: 100%;
						display: flex;
						flex-direction: column;
					}
					.ai-menu-modal .modal-body {
						flex: 1 1 auto;
						display: flex;
						flex-direction: column;
						min-height: 0;
						overflow: hidden;
					}
					.ai-stats { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
					.ai-stat { background:var(--dark-surface-2, #f4f6f9); border-radius:8px; padding:6px 10px; font-size:13px; }
					.ai-toolbar { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin-bottom:12px; }
					.ai-table-wrap {
						flex: 1 1 auto;
						min-height: 120px;
						overflow:auto;
						border:1px solid var(--dark-border, #dee2e6);
						border-radius:6px;
					}
					.ai-table { margin-bottom:0; font-size:13px; }
					.ai-table th {
						position:sticky; top:0; z-index:2; background:var(--dark-surface-2, #f8f9fa);
						white-space:nowrap; vertical-align:middle;
					}
					.ai-table td { vertical-align:middle; }
					.ai-row-menu td { background:var(--dark-surface-3, #eef2f6); font-weight:600; }
					.ai-row-ok td { background:var(--dark-surface, #fff); }
					.ai-row-fix td { background:var(--dark-tint-warning, #fff8db); }
					.ai-row-fix.ai-row-menu td { background:var(--dark-tint-warning, #ffe9a8); }
					.ai-row-delete td { background:var(--dark-tint-danger, #fde8e8) !important; color:var(--dark-fg-danger, #b42318); }
					.ai-row-delete.ai-row-menu td { background:var(--dark-tint-danger, #f8d7da) !important; }
					.ai-cell-old { color:var(--dark-text-muted, #6c757d); text-decoration:line-through; font-size:12px; }
					.ai-cell-new { color:var(--dark-fg-warning, #856404); font-weight:600; }
					.ai-badge {
						display:inline-block; border-radius:999px; padding:2px 8px;
						font-size:11px; font-weight:600; white-space:nowrap;
					}
					.ai-badge-ok { background:var(--dark-tint-success, #e7f8ef); color:var(--dark-fg-success, #16a34a); }
					.ai-badge-fix { background:var(--dark-tint-warning, #fff3cd); color:var(--dark-fg-warning, #856404); border:1px solid var(--dark-tint-border-warning, #ffe69c); }
					.ai-badge-delete { background:var(--dark-tint-danger, #f8d7da); color:var(--dark-fg-danger, #b42318); border:1px solid var(--dark-tint-border-danger, #f1aeb5); }
					.ai-folder {
						display:inline-flex; align-items:center; gap:6px;
					}
					.ai-folder i { color:var(--dark-text-muted, #64748b); font-size:16px; }
					.ai-child {
						display:inline-flex; align-items:center; gap:6px;
						padding-left:22px; font-weight:400;
					}
					.ai-child-line {
						color:var(--dark-text-muted, #94a3b8); font-family:monospace; margin-right:2px;
					}
					.ai-meta { color:var(--dark-text-muted, #6c757d); font-weight:400; font-size:12px; }
				`}</style>

				<div className="row g-2 mb-3">
					<div className="col-md-8">
						<label>Язык</label>
						<Select
							options={languages}
							value={language}
							onChange={setLanguage}
							getOptionLabel={(option) => option.native_name || option.name}
							getOptionValue={(option) => option.id}
							placeholder="Выберите язык"
							isClearable={false}
						/>
					</div>
					<div className="col-md-4 d-flex align-items-end">
						<button
							type="button"
							className="btn btn-primary w-100"
							disabled={!language || loading}
							onClick={runAi}>
							{loading ? 'Обработка...' : 'Заполнить и проверить'}
						</button>
					</div>
				</div>

				<div className="mb-3 fz-14 text-muted">
					Один запрос: заполняет контент и проверяет правила Яндекс.Еды.
					Размер/литраж/порцию из названия убирает и переносит в вес и ед. изм.
					Жёлтый — правка, красный — удалить (алкоголь / запрещено), зелёный — ок.
					{' '}
					<a href="https://yandex.ru/project/yandex.eda/content/standards" target="_blank" rel="noreferrer">
						Правила
					</a>
				</div>

				{result.stats &&
					<div className="ai-stats">
						<div className="ai-stat"><strong>{result.stats.menus}</strong> категорий</div>
						<div className="ai-stat"><strong>{result.stats.products}</strong> блюд</div>
						<div className="ai-stat"><strong>{result.stats.products_with_changes}</strong> с правками</div>
						{!!result.stats.delete_count &&
							<div className="ai-stat" style={{ background: 'var(--dark-tint-danger, #fde8e8)', color: 'var(--dark-fg-danger, #b42318)' }}>
								<strong>{result.stats.delete_count}</strong> на удаление
							</div>
						}
						<div className="ai-stat"><strong>{selectedCount}</strong> выбрано</div>
					</div>
				}

				{result.catalog.length > 0 &&
					<div className="ai-toolbar">
						<button type="button" className="btn btn-sm btn-outline-primary" onClick={() => selectAllChanges(true)}>
							Выбрать все правки
						</button>
						<button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => selectAllChanges(false)}>
							Снять все
						</button>
						<button
							type="button"
							className={`btn btn-sm ${onlyChanges ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setOnlyChanges(v => !v)}>
							{onlyChanges ? 'Показать всё меню' : 'Только с правками'}
						</button>
					</div>
				}

				{visibleCatalog.length > 0 &&
					<div className="ai-table-wrap table-responsive">
						<table className="table table-bordered table-sm ai-table">
							<thead>
								<tr>
									<th style={{ width: 42 }} className="text-center">✓</th>
									<th style={{ width: 90 }}>Статус</th>
									<th style={{ minWidth: 200 }}>Категория / Блюдо</th>
									<th style={{ minWidth: 150 }}>Название</th>
									<th style={{ minWidth: 200 }}>Описание</th>
									<th style={{ width: 90 }}>Вес</th>
									<th style={{ width: 70 }}>Ед.</th>
								</tr>
							</thead>
							<tbody>
								{visibleCatalog.map(menu => {
									const menuDelete = !!menu.delete_suggested
									const menuFix = !!menu.has_name_change
									const menuRowClass = menuDelete
										? 'ai-row-menu ai-row-delete'
										: (menuFix ? 'ai-row-menu ai-row-fix' : 'ai-row-menu ai-row-ok')

									return (
										<React.Fragment key={menu.id}>
											<tr className={menuRowClass}>
												<td className="text-center">
													{(menuDelete || menuFix) ? (
														<input
															type="checkbox"
															checked={!!menu.selected}
															onChange={() => toggleMenu(menu.id)}
														/>
													) : null}
												</td>
												<td>
													{menuDelete ? (
														<span className="ai-badge ai-badge-delete">удалить</span>
													) : menu.has_changes ? (
														<span className="ai-badge ai-badge-fix">правка</span>
													) : (
														<span className="ai-badge ai-badge-ok">ок</span>
													)}
												</td>
												<td>
													<div className="ai-folder">
														<i className="uil uil-folder"></i>
														<span>{menu.name}</span>
													</div>
													<div className="ai-meta">
														{menu.product_count} блюд
														{menu.change_count > 0 ? ` · ${menu.change_count} правок` : ''}
														{menuDelete && menu.delete_reason ? ` · ${menu.delete_reason}` : ''}
													</div>
												</td>
												<td>
													{menuFix ? (
														<CellValue current={menu.name} suggested={menu.suggested_name} changed />
													) : menuDelete ? (
														<span>будет удалена</span>
													) : (
														<span className="ai-meta">категория</span>
													)}
												</td>
												<td colSpan={3} className="ai-meta">
													{menuDelete ? (menu.delete_reason || 'запрещено') : '—'}
												</td>
											</tr>

											{menu.products.length === 0 &&
												<tr className="ai-row-ok">
													<td></td>
													<td><span className="ai-badge ai-badge-ok">ок</span></td>
													<td>
														<span className="ai-child">
															<span className="ai-child-line">└</span>
															<span className="ai-meta">Нет блюд</span>
														</span>
													</td>
													<td colSpan={4} className="ai-meta">—</td>
												</tr>
											}

											{menu.products.map((product, pIndex) => {
												const nameField = getField(product, 'name')
												const descriptionField = getField(product, 'description')
												const weightField = getField(product, 'weight')
												const unitField = getField(product, 'unit')
												const isLast = pIndex === menu.products.length - 1
												const rowClass = product.delete_suggested
													? 'ai-row-delete'
													: (product.has_changes ? 'ai-row-fix' : 'ai-row-ok')

												return (
													<tr key={product.id} className={rowClass}>
														<td className="text-center">
															{product.has_changes ? (
																<input
																	type="checkbox"
																	checked={!!product.selected}
																	onChange={() => toggleProduct(menu.id, product.id)}
																/>
															) : null}
														</td>
														<td>
															{product.delete_suggested ? (
																<span className="ai-badge ai-badge-delete">
																	{product.selected ? 'удалить' : 'удалить'}
																</span>
															) : product.has_changes ? (
																<span className="ai-badge ai-badge-fix">
																	{product.selected ? 'выбрано' : 'правка'}
																</span>
															) : (
																<span className="ai-badge ai-badge-ok">ок</span>
															)}
														</td>
														<td>
															<span className="ai-child">
																<span className="ai-child-line">{isLast ? '└' : '├'}</span>
																<span>{product.name}</span>
															</span>
															{product.delete_suggested && product.delete_reason &&
																<div className="ai-meta" style={{ paddingLeft: 34 }}>
																	{product.delete_reason}
																</div>
															}
														</td>
														<td>
															{product.delete_suggested ? (
																<span>будет удалено</span>
															) : (
																<CellValue
																	current={nameField.current}
																	suggested={nameField.suggested}
																	changed={nameField.changed}
																/>
															)}
														</td>
														<td>
															{product.delete_suggested ? '—' : (
																<CellValue
																	current={descriptionField.current}
																	suggested={descriptionField.suggested}
																	changed={descriptionField.changed}
																/>
															)}
														</td>
														<td>
															{product.delete_suggested ? '—' : (
																<CellValue
																	current={weightField.current}
																	suggested={weightField.suggested}
																	changed={weightField.changed}
																/>
															)}
														</td>
														<td>
															{product.delete_suggested ? '—' : (
																<CellValue
																	current={unitField.current}
																	suggested={unitField.suggested}
																	changed={unitField.changed}
																/>
															)}
														</td>
													</tr>
												)
											})}
										</React.Fragment>
									)
								})}
							</tbody>
						</table>
					</div>
				}

				{!loading && result.catalog.length === 0 && result.stats &&
					<div className="text-muted">Результат пустой. Попробуйте ещё раз.</div>
				}
			</Modal.Body>
			<Modal.Footer className="d-flex justify-content-between">
				<div className="text-muted fz-13">
					{selectedCount > 0 ? `К применению: ${selectedCount}` : 'Выберите правки галочками'}
				</div>
				<div className="d-flex gap-2">
					<button type="button" className="btn btn-outline-warning" onClick={onHide}>
						{t('cancel')}
					</button>
					<button
						type="button"
						className="btn btn-primary"
						disabled={selectedCount === 0 || applying}
						onClick={applyChanges}>
						Применить ({selectedCount})
					</button>
				</div>
			</Modal.Footer>
		</Modal>
	)
}

export default ExternalMenuAiModal
